
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ThumbsUp, ImageIcon } from "lucide-react";
import { fetchReportPhotos } from "@/utils/reportPhotos";

interface ReportDetailsProps {
  report: {
    id?: number;
    title: string;
    category: string;
    description: string;
    date: string;
    status: "pending" | "in-progress" | "resolved";
    supports: number;
  };
  isOpen: boolean;
  onClose: () => void;
  onSupport: () => void;
}

const ReportDetails: React.FC<ReportDetailsProps> = ({ report, isOpen, onClose, onSupport }) => {
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && report.id) {
      fetchReportPhotos(report.id).then(setPhotos).catch(() => setPhotos([]));
    } else {
      setPhotos([]);
    }
  }, [isOpen, report.id]);

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    "in-progress": "bg-blue-100 text-blue-800 border border-blue-200",
    resolved: "bg-green-100 text-green-800 border border-green-200",
  };

  const statusTranslations = {
    pending: "En attente de solution",
    "in-progress": "En cours",
    resolved: "Résolu",
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-white/95 backdrop-blur-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {report.title}
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-gray-100/80">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
              {report.category}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[report.status]}`}>
              {statusTranslations[report.status]}
            </span>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {photos.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Photo ${i + 1} du signalement`}
                  className="rounded-lg object-cover aspect-video w-full"
                  loading="lazy"
                />
              ))}
            </div>
          )}

          <p className="text-gray-700 leading-relaxed">{report.description}</p>
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <div className="text-sm text-gray-500">Signalé le {report.date}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={onSupport}
              className="flex flex-col items-center gap-1 hover:border-primary/30 hover:bg-primary/5 transition-colors py-2"
            >
              <span className="text-xs group-hover:text-primary transition-colors">
                Soutenir
              </span>
              <div className="flex items-center gap-2">
                <ThumbsUp className="h-4 w-4 group-hover:text-primary transition-colors" />
                <span className="text-xs group-hover:text-primary transition-colors">
                  ({report.supports})
                </span>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDetails;
